/** @jsxImportSource @emotion/react */
import { faCaretDown, faCaretUp, faExchange, faLink, faPlus } from "@fortawesome/pro-regular-svg-icons";
import Papa from "papaparse";
import PropTypes from "prop-types";
import { useEffect, useMemo, useState } from "react";
import { DndProvider, useDrag, useDrop } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";
import { useDispatch, useSelector } from "react-redux";
import { Link, useParams } from "react-router-dom";
import {
    FormGroup, FormText,
    Input,
    Label,
    Modal, ModalBody, ModalFooter, ModalHeader, Table
} from "reactstrap";
import { LAYER_STAGE } from "../../../actions/Layers/constants";
import { formatDateForFilename } from "../Layers/fields/formatters";
import { getTenant } from "../../../actions/Tenants/actions";
import useConfig, { useEDDateFormats } from "../../../actions/Tenants/config/configHook";
import { usePagination } from "../../../hooks";
import {
    useCreateFacilityLocation,
    useDeleteAllTenantFacilityLocations,
    useDeleteTenantFacilityLocation,
    useImportTenantFacilityLocations,
    useTenantFacilityLocations,
    useUpdateTenantFacilityLocation,
    useUpdateTenantFacilityLocationPositions,
} from "../../../hooks/useFacilityLocationQueries";
import { isFilledArray } from "../../../utils";
import { FromLink } from "../../../utils/useNavigateFrom";
import MetaForm from "../../Forms/MetaForm";
import { Button } from "../../Helper/Button";
import { FormContainer } from "../../Helper/FormContainer";
import { Loading } from "../../Helper/Loading";
import { PaginationRow } from "../../Helper/PaginationRow";
import FullPageLayout from "../Layout/FullPageLayout";
import RoomLinksModal from "./RoomLinksModal";
import TenantPageWrappedInConfigProvider from "./TenantPageWrappedInConfigProvider";

const defaultValues = {
    label: "",
    type: null,
    capacity: null,
    stage: null,
    external_id: null,
    gateway: null,
    controller_room: null,
};

// * Wrap this page in the config of the tenant we are editing
export default function WrappedInConfigProviderTenantEditForm() {
    return <TenantPageWrappedInConfigProvider>
        <TenantEditFacilityLocationsForm />
    </TenantPageWrappedInConfigProvider>;
}

const RIPENING_ROOM = "ripening_room";
const STORAGE_ROOM = "storage_room";
const DOCK = "dock";
const VIRTUAL = "virtual";
const OTHER = "other";

// TODO: define non ripening cell types
export const LOCATION_TYPES = {
    RIPENING_ROOM,
    STORAGE_ROOM,
    DOCK,
    VIRTUAL,
    OTHER,
};

export const LOCATION_TYPE_LABELS = {
    [RIPENING_ROOM]: "Ripening room",
    [STORAGE_ROOM]: "Storage room",
    [DOCK]: "Dock",
    [VIRTUAL]: "Virtual",
    [OTHER]: "Other",
};

// DraggableTableRow component for drag and drop functionality
const DraggableTableRow = ({ index, children, onUpdatePosition }) => {
    const [, ref] = useDrop({
        accept: "facility-location",
        drop(item) {
            if (item.index !== index) {
                onUpdatePosition(item.index, index);
            }
        },
    });

    const [, drag] = useDrag({
        type: "facility-location",
        item: { index },
    });

    return (
        <tr ref={(node) => drag(ref(node))} style={{ cursor: "move" }}>
            {children}
        </tr>
    );
};

DraggableTableRow.propTypes = {
    index: PropTypes.number.isRequired,
    children: PropTypes.node.isRequired,
    onUpdatePosition: PropTypes.func.isRequired,
};

function TenantEditFacilityLocationsForm() {
    const current = useSelector((state) => state.tenants.current);
    const [isOpen, setModal] = useState(false);
    const [importModal, setImportModal] = useState(false);
    const [roomLinksOpen, setRoomLinksOpen] = useState(false);
    const params = useParams();
    const dispatch = useDispatch();
    const [query, setQuery] = useState(defaultValues);
    const [showAllToBeImportedItems, setShowAllToBeImportedItems] = useState(false);
    const config = useConfig();
    const edFormats = useEDDateFormats();
    const [filter, setFilter] = useState({ stage: [], type: [], facility: [] });
    const { offset, limit, onPageChanged } = usePagination();


    const { tenant_name } = current;
    const { tenant_id } = params;

    const [importData, setImportData] = useState([]);
    const [selectedFile, setSelectedFile] = useState(false);

    // Build server-side filter params from local filter state
    const serverFilter = useMemo(() => {
        const f = { offset, limit };
        if (isFilledArray(filter.stage)) f.stage = filter.stage.join(",");
        if (isFilledArray(filter.type)) f.type = filter.type.join(",");
        if (isFilledArray(filter.facility)) f.facility = filter.facility.join(",");
        return f;
    }, [offset, limit, filter.stage, filter.type, filter.facility]);

    // Tanstack query for facility locations
    const { data: locationsData, isLoading } = useTenantFacilityLocations(tenant_id, serverFilter);
    const locations = locationsData?.results;
    const locationsCount = locationsData?.count ?? 0;

    // Mutations
    const createMutation = useCreateFacilityLocation();
    const updateMutation = useUpdateTenantFacilityLocation();
    const deleteMutation = useDeleteTenantFacilityLocation();
    const deleteAllMutation = useDeleteAllTenantFacilityLocations();
    const importMutation = useImportTenantFacilityLocations();
    const positionsMutation = useUpdateTenantFacilityLocationPositions();
    const isMutating = createMutation.isPending || updateMutation.isPending || deleteMutation.isPending
        || deleteAllMutation.isPending || importMutation.isPending || positionsMutation.isPending;

    // * refresh the tenant object if needed
    useEffect(() => {
        if (tenant_id) {
            dispatch(getTenant(tenant_id));
        }
    }, [tenant_id]);

    // Handle position updates for drag and drop
    const onUpdatePosition = (fromIndex, toIndex) => {
        if (fromIndex === toIndex) return;

        const updatedLocations = [...(locations || [])];
        const [item] = updatedLocations.splice(fromIndex, 1);
        updatedLocations.splice(toIndex, 0, item);

        // Update the backend with the new order
        const positionUpdates = updatedLocations.map((location, index) => ({
            location_id: location.location_id,
            position: index + 1
        }));

        positionsMutation.mutate({ tenantId: tenant_id, positions: positionUpdates });
    };

    // Handle position up/down button clicks
    const positionUp = (index) => {
        if (index === 0) return; // Cannot move first item up
        onUpdatePosition(index, index - 1);
    };

    const positionDown = (index) => {
        if (index === (locations || []).length - 1) return; // Cannot move last item down
        onUpdatePosition(index, index + 1);
    };

    const fileChangeHandler = (e) => {
        const file = e.target.files[0];

        if (file) {
            Papa.parse(file, {
                header: true,
                skipEmptyLines: true,
                complete(results) {
                    const options = results.data.map((i) => {
                        // Handle both old format (client_room) and new format (gateway, controller_room)
                        let gateway = i.gateway ? i.gateway.trim() : null;
                        let controller_room = i.controller_room ? i.controller_room.trim() : null;

                        // If client_room exists but gateway/controller_room don't, split it
                        if (i.client_room && !gateway && !controller_room) {
                            const parts = i.client_room.trim().split("_");
                            gateway = parts[0] || null;
                            controller_room = parts.slice(1).join("_") || null;
                        }

                        return {
                            ...defaultValues,
                            location_id: i.location_id ? i.location_id.trim() : null,
                            type: i.type ? i.type.trim().toLowerCase() : null,
                            capacity: i.capacity ? i.capacity : null,
                            stage: i.stage ? i.stage.trim().toLowerCase() : null,
                            label: i.label ? i.label.trim() : null,
                            external_id: i.external_id ? i.external_id.trim() : null,
                            gateway,
                            controller_room,
                        };
                    });

                    setImportData(options);
                    setSelectedFile(file);
                },
            });
        }
    };

    const handleImport = () => importMutation.mutate({ tenantId: params.tenant_id, data: filteredImportData });


    const form = [
        {
            label: "Label",
            name: "label",
            type: "text",
        },
        {
            label: "External ID",
            name: "external_id",
            type: "text",
        },
        {
            label: "Gateway",
            name: "gateway",
            type: "text",
            description: "Gateway UUID for ripening room gateway integration.",
        },
        {
            label: "Controller Room",
            name: "controller_room",
            type: "text",
            description: "Controller Room ID for ripening room gateway integration. .e.g \"1\"",

        },
        {
            label: "Type",
            name: "type",
            type: "single-select",
            options: Object.values(LOCATION_TYPES).map((i) => ({ label: i, value: i })),
        },
        {
            label: "Capacity",
            name: "capacity",
            type: "number",
        },
        {
            label: "Stage",
            name: "stage",
            type: "single-select",
            options: Object.values(LAYER_STAGE).map((i) => ({ value: i, label: i })),
        },
        {
            label: "Facility",
            name: "facility",
            type: "select-managed",
            description: <span>To manage overarching <Link to={`/tenants/${tenant_id}/form-field-options/facility`}><b>facilities</b> go here</Link>.</span>
        }
    ];

    const deleteOption = (option) => deleteMutation.mutate({ tenantId: tenant_id, locationId: option.location_id });

    const deleteAll = () => deleteAllMutation.mutate({ tenantId: tenant_id });

    const filterForm = [
        {
            label: "Stage",
            name: "stage",
            type: "single-select",
            options: Object.values(LAYER_STAGE).map((i) => ({ value: i, label: i })),
            return_array: true,
            multi: true,
            lg: 4,
        },
        {
            label: "Type",
            name: "type",
            type: "single-select",
            options: Object.values(LOCATION_TYPES).map((i) => ({ label: i, value: i })),
            return_array: true,
            multi: true,
            lg: 4,
        },
        {
            label: "Facility",
            name: "facility",
            type: "select-managed",
            return_array: true,
            multi: true,
            lg: 4,

        }
    ];
    const cancel = () => {
        setModal(false);
        setQuery(defaultValues);
    };

    const newItem = () => {
        setModal(true);
        setQuery(defaultValues);
    };
    const onSubmit = () => {
        const mutate = query.location_id
            ? () => updateMutation.mutate({ tenantId: tenant_id, data: query })
            : () => createMutation.mutate({ tenantId: tenant_id, data: query });
        mutate();
        setModal(false);
        setQuery(defaultValues);
    };


    const exportData = (data, fileName, type) => {
        // Create a link and download the file
        const blob = new Blob([data], { type });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = fileName;
        a.click();
        window.URL.revokeObjectURL(url);
    };

    const exportOptions = () => {
        const csvData = Papa.unparse(locations);
        exportData(csvData, `export-${tenant_name}-${formatDateForFilename(edFormats.datetime_format)}-facility-locations.csv`, "text/csv;charset=utf-8;");
    };

    const filteredImportData = importData.filter((i) => i.label && i.type && i.stage);

    const buttons = (
        <>
            <Button className="text-nowrap" color="primary" onClick={newItem} icon={faPlus}>Add option</Button>
            <Button className="text-nowrap" color="primary" onClick={() => setImportModal(true)} icon={faExchange}>Import</Button>
            <Button className="text-nowrap" color="primary" onClick={() => setRoomLinksOpen(true)} icon={faLink}>Room links</Button>
        </>
    );

    if (!locations && isLoading) return <Loading />;

    return (
        <>
            <FullPageLayout>
                <FormContainer
                    title="Facility Locations"
                    subtitle={<span>Edit tenant <b>{tenant_name}</b> facility locations. To manage overarching <Link to={`/tenants/${tenant_id}/form-field-options/facility`}><b>facilities</b> go here</Link>.</span>}
                    backTo={`/tenants/${tenant_id}`}
                    actionsTop={buttons}
                    fullWidth
                >

                    <div>
                        <MetaForm
                            meta={filterForm}
                            grid={true}
                            object={filter}
                            setValue={(field, value) => {
                                setFilter((oldFilter) => ({ ...oldFilter, [field]: value }));
                                onPageChanged(1);
                            }} />
                    </div>
                    <DndProvider backend={HTML5Backend}>
                        <Table size="sm">
                            <thead>
                                <tr>
                                    <th>location_id</th>
                                    <th>Type</th>
                                    <th>Capacity</th>
                                    <th>Stage</th>
                                    <th>Facility</th>
                                    <th>Label</th>
                                    <th>External ID</th>
                                    <th>Gateway</th>
                                    <th>Controller Room</th>
                                    <th></th>
                                </tr>
                            </thead>

                            <tbody>
                                {(locations || []).map((i, index) => (
                                    <DraggableTableRow key={index} index={index} onUpdatePosition={onUpdatePosition}>
                                        <td className="align-middle" title={i.location_id}>
                                            <FromLink to={`/ripening-room/${i.location_id}`}>{i.location_id.slice(0, 7)}</FromLink>
                                        </td>
                                        <td className="align-middle ">{i.type}</td>
                                        <td className="align-middle ">{i.capacity}</td>
                                        <td className="align-middle">{i.stage}</td>
                                        <td className="align-middle">{config.get_form_option_label("facility", i.facility)}</td>
                                        <td className="align-middle">{i.label}</td>
                                        <td className="align-middle">{i.external_id}</td>
                                        <td className="align-middle">{i.gateway && <FromLink to={`/device-management/${i.gateway}`}>{i.gateway}</FromLink>}</td>
                                        <td className="align-middle">{i.controller_room}</td>
                                        <td className="text-end">
                                            <Button
                                                size="sm"
                                                color="light"
                                                className="me-1 mb-1"
                                                disabled={index === 0}
                                                onClick={() => positionUp(index)}
                                                icon={faCaretUp}
                                            />

                                            <Button
                                                size="sm"
                                                color="light"
                                                className="me-1 mb-1"
                                                disabled={index === (locations || []).length - 1}
                                                onClick={() => positionDown(index)}
                                                icon={faCaretDown}
                                            />

                                            <Button
                                                color="primary"
                                                className="me-1 mb-1"
                                                outline
                                                onClick={() => {
                                                    setQuery(i);
                                                    setModal(true);
                                                }}>
                                                Edit
                                            </Button>

                                            <Button color="danger" className="me-1 mb-1" outline onClick={() => deleteOption(i)}>Delete</Button>
                                        </td>
                                    </DraggableTableRow>
                                ))}
                            </tbody>
                        </Table>
                    </DndProvider>
                    <PaginationRow totalCount={locationsCount} />
                </FormContainer>
            </FullPageLayout>

            <RoomLinksModal tenantId={tenant_id} isOpen={roomLinksOpen} toggle={() => setRoomLinksOpen(false)} />
            <Modal size="md" isOpen={isOpen} toggle={() => cancel()} >
                <ModalHeader toggle={() => cancel()} >{query.location_id ? "Edit" : "New"} Location</ModalHeader>

                <ModalBody>
                    <MetaForm meta={form} setValue={(field, value) => setQuery((query) => ({ ...query, [field]: value }))} object={query} config={config} />
                </ModalBody>

                <ModalFooter>
                    <Button color="light" onClick={() => cancel()}>Cancel</Button>
                    <Button color="primary" disabled={!query.label || !query.stage || !query.type || isMutating} className="me-1 mb-1" onClick={() => onSubmit()}>Submit</Button>
                </ModalFooter>
            </Modal >

            <Modal size="xl" isOpen={importModal} toggle={() => setImportModal(false)} >
                <ModalHeader toggle={() => setImportModal(false)} >Import / Delete all</ModalHeader>

                <ModalBody>
                    <FormGroup>
                        <Label for="exampleFile">CSV</Label>
                        <Input
                            id="exampleFile"
                            name="file"
                            type="file"
                            onChange={fileChangeHandler}
                        />

                        {selectedFile ? (
                            <div className="py-3">
                                <FormText >
                                    <div>Filename: {selectedFile.name}</div>
                                    <div>Filetype: {selectedFile.type}</div>
                                    <div>Size in bytes: {selectedFile.size}</div>
                                    <div>Items: {importData && importData.length}</div>
                                    <div>Valid items: {importData && filteredImportData.length}</div>
                                </FormText>
                            </div>

                        ) : (
                            <FormText>Upload csv file without headers here.</FormText>
                        )}
                    </FormGroup>

                    {importData.length > 0 && (
                        <Table size="sm" responsive>
                            <thead>
                                <tr>
                                    <th>location_id</th>
                                    <th>Type</th>
                                    <th>Capacity</th>
                                    <th>Stage</th>
                                    <th>Facility</th>
                                    <th>Label</th>
                                    <th>External ID</th>
                                    <th>Gateway</th>
                                    <th>Controller Room</th>
                                </tr>
                            </thead>

                            <tbody>
                                {importData.slice(0, showAllToBeImportedItems ? importData.length : 10).map((i, index) => (
                                    <tr key={index} >
                                        <td className={`align-middle`}>{i.location_id}</td>
                                        <td className={`align-middle ${i.type || "bg-danger"}`}>{i.type}</td>
                                        <td className={`align-middle ${i.capacity || "bg-warning"}`}>{i.capacity}</td>
                                        <td className={`align-middle ${i.stage || "bg-danger"}`}>{i.stage}</td>
                                        <td className={`align-middle ${i.facility || "bg-danger"}`}>{i.facility}</td>
                                        <td className={`align-middle ${i.label || "bg-danger"}`}>{i.label}</td>
                                        <td className={`align-middle ${i.external_id || "bg-warning"}`}>{i.external_id}</td>
                                        <td className={`align-middle ${i.gateway || "bg-warning"}`}>{i.gateway}</td>
                                        <td className={`align-middle ${i.controller_room || "bg-warning"}`}>{i.controller_room}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </Table>
                    )}

                    {importData.length > 10 && !showAllToBeImportedItems && (
                        <div className="text-center py-3">
                            <span onClick={() => setShowAllToBeImportedItems(true)} className="btn-link">Show all</span>
                        </div>
                    )}
                </ModalBody>

                <ModalFooter>
                    <Button color="light" onClick={() => setImportModal(false)}>Cancel</Button>
                    <Button color="light" className="me-1 mb-1" onClick={() => exportOptions()}>Export</Button>
                    <Button color="primary" disabled={isMutating || !importData || filteredImportData.length === 0} className="me-1 mb-1" onClick={() => handleImport()}>Import</Button>
                    <Button color="danger" disabled={isMutating} className="me-1 mb-1" onClick={() => deleteAll()}>Delete All</Button>
                </ModalFooter>
            </Modal>
        </>
    );
}
